import { openDB, type DBSchema } from 'idb'

interface TwoDomyDB extends DBSchema {
  calendar_entries: {
    key: string
    value: {
      id: string
      child_id: string
      family_id: string
      date: string
      location: 'home_a' | 'home_b'
      comment: string | null
      created_by: string
      created_at: string
      updated_at: string
      sync_status: 'synced' | 'pending' | 'error'
    }
    indexes: {
      'by-child': string
      'by-date': string
      'by-sync-status': string
    }
  }
  children: {
    key: string
    value: {
      id: string
      family_id: string
      name: string
      birth_date: string | null
      created_at: string
    }
    indexes: {
      'by-family': string
    }
  }
  pending_changes: {
    key: string
    value: {
      id: string
      table: string
      action: 'create' | 'update' | 'delete'
      data: any
      timestamp: number
    }
    indexes: {
      'by-table': string
      'by-timestamp': number
    }
  }
  device_keypair: {
    key: string // userId (Clerk user ID)
    value: {
      userId: string
      deviceId: string
      privateKeyJwk: JsonWebKey
      publicKeyJwk: JsonWebKey
      publicKeySpkiBase64: string
      label: string | null
      createdAt: number
    }
  }
}

export const initDB = async () => {
  return await openDB<TwoDomyDB>('2homes-db', 3, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        // Fresh install path – create all stores
        const calendarStore = db.createObjectStore('calendar_entries', { keyPath: 'id' })
        calendarStore.createIndex('by-child', 'child_id')
        calendarStore.createIndex('by-date', 'date')
        calendarStore.createIndex('by-sync-status', 'sync_status')

        const childrenStore = db.createObjectStore('children', { keyPath: 'id' })
        childrenStore.createIndex('by-family', 'family_id')

        const pendingStore = db.createObjectStore('pending_changes', { keyPath: 'id' })
        pendingStore.createIndex('by-table', 'table')
        pendingStore.createIndex('by-timestamp', 'timestamp')
      }

      if (oldVersion < 2) {
        // v1 → v2: przebudowa store 'calendar_entries' (usunięcie pola segment)
        if (db.objectStoreNames.contains('calendar_entries')) {
          db.deleteObjectStore('calendar_entries')
        }
        const calendarStoreV2 = db.createObjectStore('calendar_entries', { keyPath: 'id' })
        calendarStoreV2.createIndex('by-child', 'child_id')
        calendarStoreV2.createIndex('by-date', 'date')
        calendarStoreV2.createIndex('by-sync-status', 'sync_status')
      }

      if (oldVersion < 3) {
        // v2 → v3: E2EE rewrite (device-bound ECDH keys, drop old password-based store)
        // Cast: the old 'encryption_keys' store no longer exists in the typed schema.
        const legacyStore = 'encryption_keys' as never
        if (db.objectStoreNames.contains(legacyStore)) {
          db.deleteObjectStore(legacyStore)
        }
        db.createObjectStore('device_keypair', { keyPath: 'userId' })
      }
    },
  })
}

// Singleton instance
let dbPromise: ReturnType<typeof initDB> | null = null

export const getDB = () => {
  if (!dbPromise) {
    dbPromise = initDB()
  }
  return dbPromise
} 